import React from 'react';
import { ArrowRight, FileJson, GitCompare, Lock, Users } from 'lucide-react';
import { Page, Section } from './PageColumn';
import { ShotVisual } from './FeatureVisuals';
import { useIsDark } from './motion';

// Each step shows the screen it actually happens on. The four steps were four
// paragraphs of prose, and a reader with no picture of the product has to take
// "a person accepts the limits, by name" entirely on faith — where a crop of
// the approval screen shows them what that means in one look.
const STEPS = [
  {
    n: '1',
    title: 'Describe the agent, before it exists',
    body: 'In ordinary language: what it will do, what it will touch, what it will be allowed to spend. No code has been written yet.',
    shot: 'describe',
    alt: 'A plain-language description of an agent typed into the assessment box',
    shift: 0
  },
  {
    n: '2',
    title: 'The method works out what that implies',
    body: 'Which capabilities the description grants — money movement, vendor records, system administration, external communication — and how far the damage would reach if it went wrong.',
    shot: 'boundaries',
    alt: 'The operating boundaries derived from that description: allowed actions, blocked actions and a spend limit',
    shift: 0
  },
  {
    n: '3',
    title: 'A person accepts the limits, by name',
    body: 'Someone reads the boundaries, edits them, and signs. Until they do, the agent cannot act on its own — enforced, not advised.',
    shot: 'profiles',
    alt: 'A safety profile showing who approved it and when',
    shift: 0
  },
  {
    n: '4',
    title: 'The limits are enforced by something that cannot be argued with',
    body: 'The engine never reads the model\u2019s reasoning, only the action it is attempting. There is no prompt that talks it into allowing what the profile forbids.',
    shot: 'decision-block',
    alt: 'A refused action, with the rule that refused it named',
    shift: 0
  }
];

export const Method: React.FC<{ setActiveTab: (tab: string) => void }> = ({ setActiveTab }) => {
  const dark = useIsDark();
  return (
  <Page className="space-y-12">

    {/* The title sits in the same rail every section heading does, so the page
        has one left edge for structure and one for prose. */}
    <header className="grid gap-x-12 gap-y-4 xl:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
      <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Why this exists</h1>
      <div className="max-w-3xl space-y-4">
      <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-200">
        Software is increasingly written by AI agents that can also <em>act</em> — pay invoices, change vendor
        bank details, alter production systems, send data outside the company. The instructions telling such an
        agent what it must never do are usually written in the same place as the instructions telling it what to
        do: a prompt.
      </p>
      <p className="text-base leading-relaxed text-slate-600 dark:text-slate-300">
        That is the problem. Boundaries written in a prompt are expressed in the one medium an agent is easiest
        to talk out of, decided by whoever happened to write the prompt, at the moment they wrote it, usually
        with no record that anybody weighed the decision at all.
      </p>
      </div>
    </header>

    <Section title="What the method does instead" wide>
      <p className="max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
        It moves the decision earlier, and outside the model. Four steps:
      </p>
      <ol className="grid gap-4 xl:grid-cols-2">
        {STEPS.map((step) => (
          <li key={step.n} className="glass-panel flex flex-col gap-3 rounded-2xl p-5">
            <ShotVisual src={step.shot} alt={step.alt} dark={dark} shift={step.shift} height={150} />
            <div className="flex gap-4">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-brand-100 text-sm font-black text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                {step.n}
              </span>
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">{step.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{step.body}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </Section>

    <Section title="Why it is not just this website" wide>
      <p className="max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
        The useful part of this work is the method, not the implementation. So the profile format is published as
        an open specification that anyone can implement, in any language, on their own infrastructure, with no
        dependency on this project continuing to exist.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { icon: FileJson, title: 'A written specification', body: 'What a conforming engine must do, in normative language, with the reasoning for each rule.' },
          { icon: GitCompare, title: 'A conformance suite', body: 'Test vectors written from the specification, not from our code, so passing means agreeing with the spec — not with us.' },
          { icon: Lock, title: 'Apache-2.0', body: 'Implement it freely, including commercially, with an express patent grant.' }
        ].map((item) => (
          <div key={item.title} className="glass-panel rounded-2xl p-5">
            <item.icon className="h-5 w-5 text-brand-600 dark:text-brand-300" />
            <p className="mt-2 text-sm font-bold text-slate-900 dark:text-white">{item.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{item.body}</p>
          </div>
        ))}
      </div>
      <p className="max-w-3xl text-sm text-slate-600 dark:text-slate-300">
        The schema and the conformance vectors are served directly, so another implementation can fetch them
        without cloning anything:{' '}
        <a className="font-mono text-xs font-semibold text-brand-600 underline underline-offset-2 dark:text-brand-300" href="/schema/agenticscale-profile-v1.json">
          /schema/agenticscale-profile-v1.json
        </a>{' '}
        and{' '}
        <a className="font-mono text-xs font-semibold text-brand-600 underline underline-offset-2 dark:text-brand-300" href="/spec/conformance/vectors.json">
          /spec/conformance/vectors.json
        </a>.
      </p>
    </Section>

    <Section title="Who it is for">
      <div className="glass-panel rounded-2xl p-5">
        <Users className="h-5 w-5 text-brand-600 dark:text-brand-300" />
        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          A developer building an agent alone, a small team without a security function, a regulated organization
          that needs to show who authorized what, and anyone who has to review an agent somebody else built. The
          method needs no account, no vendor relationship, and no data leaving your own machine.
        </p>
      </div>
    </Section>

    <section className="glass-panel rounded-2xl p-6">
      <h2 className="text-lg font-bold text-slate-900 dark:text-white">What is proven, and what is not</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
        This page describes intent. The claims register lists every capability with the thing that backs it, every
        gap this project has not closed — including that its own benchmark corpus is self-authored and that no
        outside organization has yet adopted it — and the defects this project has found in its own work.
      </p>
      <button
        type="button"
        onClick={() => setActiveTab('verify')}
        className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-brand-500"
      >
        Open the claims register <ArrowRight className="h-4 w-4" />
      </button>
    </section>
  </Page>
);
};
